import React, { useContext, forwardRef } from 'react';
import { NavContext } from 'Context/nav-context';
import clsx from 'clsx';
import Icons from '../icons';
import { useIntl } from 'react-intl';
import { chevronRightIcon, KEY } from 'Data/constants';
import Icon from '@lds/icon';
import { A11yList } from 'Components/shared/index';
import { formatLink } from '@/utils';
import { useDetermineBanner, useDetermineEnvironment } from '@/hooks';

//styles
import styles from './mobile-sub-nav.module.scss';
import { CMSData } from '@/types/cmsData';

interface SubNavProps {
  currentIndex: number;
  level: string;
  navigationMobile: CMSData[];
  accountLinks: CMSData[];
  setCurrentIndex: React.Dispatch<number>;
  toggleMobileNav?: () => void;
}

// TODO: fix strange behaviour when using a combination of TAB and arrow keys to traverse navigation
const SubNav = forwardRef<HTMLUListElement, SubNavProps>(
  ({ level, navigationMobile, accountLinks, setCurrentIndex, currentIndex }, ref) => {
    const {
      category: {
        ['mobileUid']: [mobileUid],
        ['L0']: [L0],
        ['L1']: [L1, setL1],
        ['L2']: [L2, setL2],
      },
    } = useContext(NavContext);

    const { formatMessage } = useIntl();

    const isPhxBanner = useDetermineBanner();
    const env = useDetermineEnvironment();

    const navL0 = navigationMobile
      ?.filter((node) => node.uid === mobileUid)[0]
      ?.child?.filter((node) => node.uid === L0)[0];
    const navL1 = navL0?.child;
    const navL2 = navL1?.filter((node) => node.uid === L1)[0]?.child;
    const navL3 = navL2?.filter((node) => node.uid === L2)[0]?.child;

    let navNode = level === 'L0' ? navL1 : level === 'L1' ? navL2 : navL3;

    if (L0 === 'accountLinks') {
      navNode = accountLinks;
    }

    const navName =
      level === 'L0'
        ? navL0?.name
        : level === 'L1'
        ? navL1?.filter((node) => node.uid === L1)[0]?.name
        : navL2?.filter((node) => node.uid === L2)[0]?.name;
    const navLink =
      level === 'L0'
        ? navL0?.link
        : level === 'L1'
        ? navL1?.filter((node) => node.uid === L1)[0]?.link
        : navL2?.filter((node) => node.uid === L2)[0]?.link;

    const onSelection = ({
      event,
      hasChildren,
      uid,
    }: {
      event: React.KeyboardEvent | React.MouseEvent;
      hasChildren: boolean | null;
      uid: string;
    }) => {
      const key = (event as React.KeyboardEvent).key || event.type;

      if (!hasChildren && key === KEY.ENTER) {
        if (event.target instanceof HTMLElement && event.target.children[0] instanceof HTMLElement) {
          event.target.children[0].click();
        }

        return;
      }

      if (hasChildren && (key === 'click' || key === KEY.ARROW_RIGHT || key === KEY.ENTER)) {
        event.preventDefault();

        document.getElementById('carouselOuterContainer')!.scrollTop = 0;

        if (level === 'L0') {
          setL1(uid);
          setCurrentIndex(2);
        }
        if (level === 'L1') {
          setL2(uid);
          setCurrentIndex(3);
        }
      }
    };

    return (
      <nav id={'hamburger__subnav' + level} className={clsx(styles.mobileSubNavContainer)}>
        <div className={styles.navHeaderLayout}>
          <button
            className={styles.navPreviousMenu}
            onClick={() => {
              document.getElementById('carouselOuterContainer')!.scrollTop = 0;
              setCurrentIndex(currentIndex - 1);
            }}
          >
            <Icon
              classes={styles.chevronLeft}
              alt={formatMessage({ id: 'leftArrow' })}
              src={chevronRightIcon}
              width={16}
              height={16}
            />
            <span>{navName ? navName : formatMessage({ id: 'myAccount' })}</span>
          </button>
          <A11yList
            id={`mobileMenuSubNav${level}`}
            listStyle={styles.navItemsLayout}
            ariaLabel={formatMessage({ id: 'breadcrumbsShop' })}
            skipFocus={true}
            trapFocus={true}
            forwardedRef={ref}
          >
            {L0 !== 'accountLinks' ? (
              <li
                role="presentation"
                className={clsx(styles.navItemL0, {
                  [styles.wrapLineHeight]: navName?.length !== undefined && navName.length > 25,
                })}
                key={mobileUid}
                id={mobileUid}
                tabIndex={0}
                onClick={(event) => onSelection({ event, hasChildren: false, uid: mobileUid })}
                onKeyDown={(event) => onSelection({ event, hasChildren: false, uid: mobileUid })}
              >
                <a
                  href={formatLink(navLink, isPhxBanner, env)}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <span
                    className={clsx({ [styles.wrapLineHeight]: navName?.length !== undefined && navName?.length > 25 })}
                  >
                    {formatMessage({ id: 'seeAll' }) + navName ?? ''}
                  </span>
                </a>
              </li>
            ) : null}
            {navNode?.map(({ name, uid, link, hasChildren }) => {
              if (uid.includes('ShopBy') || uid.includes('Featured')) return;
              return (
                <li
                  role="presentation"
                  className={clsx(styles.navItemL0, name.length > 25 && styles.wrapLineHeight)}
                  key={uid}
                  id={uid}
                  onClick={(event) => onSelection({ event, hasChildren, uid })}
                  onKeyDown={(event) => onSelection({ event, hasChildren, uid })}
                >
                  <a href={formatLink(link, isPhxBanner, env)}>
                    <span
                      className={clsx({
                        [styles.wrapLineHeight]: navName?.length !== undefined && navName?.length > 25,
                      })}
                    >
                      {name}
                    </span>
                    {hasChildren ? (
                      <Icon
                        classes={styles.chevronRight}
                        alt={formatMessage({ id: 'rightArrow' })}
                        src={chevronRightIcon}
                        width={8}
                        height={8}
                      />
                    ) : null}
                  </a>
                </li>
              );
            })}
            {level === 'L1' || level === 'L2' ? (
              <React.Fragment>
                {navL2?.map((item) => {
                  if (!item?.uid?.includes('ShopBy') && !item?.uid?.includes('Featured')) return;
                  if (!item?.hasChildren) return;
                  return (
                    <React.Fragment key={item?.uid}>
                      <li className={clsx(styles.navItem, styles.selectedNav)} key={item?.uid} id={item?.uid}>
                        {item?.name}
                      </li>
                      {item?.child?.map(({ name, uid, link, hasChildren }) => {
                        if (!hasChildren && uid?.includes('ShopBy') && uid?.includes('Featured')) return;

                        return (
                          <li className={clsx(styles.navItemL0)} key={uid} id={uid}>
                            <a href={formatLink(link, isPhxBanner, env)}>{name}</a>
                          </li>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ) : null}
            <Icons />
          </A11yList>
        </div>
      </nav>
    );
  }
);

SubNav.displayName = 'SubNav';

export default SubNav;
